import React, { useState } from "react";
import { useAuth } from "../../lib/AuthContext";

export const TEAM = [
  "Nayan",
  "Manish",
  "Puru",
  "Ritesh",
  "Yaswanth",
  "Nawang",
] as const;
export type TeamMember = (typeof TEAM)[number];

export type ServiceKey =
  | "Chat API"
  | "HR Agent Name API"
  | "Auth API"
  | "Files API"
  | "Emailer API";

export type MaintenanceScope = "Global" | "Service";

export type AddRequestForm = {
  scope: MaintenanceScope;
  target: string;
  startISO: string;
  endISO: string;
  requestedBy: TeamMember | "";
  notes: string;
};

export type AddRequestPayload = Omit<AddRequestForm, "requestedBy"> & {
  requestedBy: TeamMember;
};

function classNames(...xs: (string | false | null | undefined)[]) {
  return xs.filter(Boolean).join(" ");
}

const DEFAULT_GLOBAL_TARGET = "rolesgpt.com + hr.rolesgpt.com";
const DEFAULT_SERVICE: ServiceKey = "Chat API";

export default function AddMaintenanceModal({
  open,
  onClose,
  onCreate,
  team = TEAM as readonly TeamMember[],
  services = [
    "Chat API",
    "HR Agent Name API",
    "Auth API",
    "Files API",
    "Emailer API",
  ] as const,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (data: AddRequestPayload) => void;
  team?: readonly TeamMember[];
  services?: readonly ServiceKey[];
  initial?: Partial<AddRequestForm>;
}) {
  const [form, setForm] = useState<AddRequestForm>({
    scope: initial?.scope ?? "Global",
    target:
      initial?.target ??
      (initial?.scope === "Service" ? DEFAULT_SERVICE : DEFAULT_GLOBAL_TARGET),
    startISO: initial?.startISO ?? "",
    endISO: initial?.endISO ?? "",
    requestedBy: initial?.requestedBy ?? "",
    notes: initial?.notes ?? "",
  });

  const { hasAuthenticated } = useAuth();
  const apiURL = process.env.REACT_APP_API_URL

  function setScope(next: MaintenanceScope) {
    setForm((f) => ({
      ...f,
      scope: next,
      target:
        next === "Global"
          ? DEFAULT_GLOBAL_TARGET
          : services[0] ?? DEFAULT_SERVICE,
    }));
  }

  function validate(): string | null {
    if (!form.startISO || !form.endISO || !form.requestedBy) {
      return "Please complete all required fields.";
    }
    const start = new Date(form.startISO);
    const end = new Date(form.endISO);
    if (isNaN(start.getTime()) || isNaN(end.getTime()))
      return "Invalid date/time.";
    if (end <= start) return "End must be after Start.";
    return null;
  }

  async function handleCreate() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const payload = {
      app_name: form.target,
      requested_by: form.requestedBy,
      type: form.scope,
      start_time: new Date(form.startISO).toISOString(),
      end_time: new Date(form.endISO).toISOString(),
      notes: form.notes, // include only if API supports it
    };

    if (!hasAuthenticated) return;
    console.log(payload)
    try {
      const res = await fetch(`${apiURL}/site-event`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Token: hasAuthenticated, // replace with actual token
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const error = await res.json();
        alert("Error: " + JSON.stringify(error));
        return;
      }

      const data = await res.json();
      onCreate(data); // optional if you want to lift response up
      alert("Request created successfully!");

      // reset form
      setForm({
        scope: "Global",
        target: DEFAULT_GLOBAL_TARGET,
        startISO: "",
        endISO: "",
        requestedBy: "",
        notes: "",
      });
      onClose();
    } catch (e) {
      console.error(e);
      alert("Network error: " + e);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-request-title"
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl border p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 id="add-request-title" className="text-lg font-semibold">
              New Maintenance Request
            </h2>
            <p className="text-xs text-neutral-500">
              Fill the form. On approval, your system will email all registered
              users.
            </p>
          </div>
          <button
            onClick={onClose}
            className="px-2 py-1 text-neutral-500 hover:text-black"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-1">
            <label className="text-sm font-medium">Scope</label>
            <div className="mt-2 flex gap-2">
              {(["Global", "Service"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  className={classNames(
                    "px-3 py-1.5 rounded-xl border text-sm",
                    form.scope === s ? "bg-black text-white" : "bg-white"
                  )}
                  type="button"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-1">
            <label className="text-sm font-medium">
              Requested By<span className="text-red-500"> *</span>
            </label>
            <select
              value={form.requestedBy}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  requestedBy: e.target.value as TeamMember,
                }))
              }
              className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
            >
              <option value="">Select a member</option>
              {team.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="col-span-1">
            <label className="text-sm font-medium">
              {form.scope === "Global" ? "Domains" : "Service"}
            </label>
            {form.scope === "Global" ? (
              <input
                value={form.target}
                readOnly
                className="mt-2 w-full border rounded-xl px-3 py-2 text-sm bg-neutral-100"
              />
            ) : (
              <select
                value={form.target}
                onChange={(e) =>
                  setForm((f) => ({ ...f, target: e.target.value }))
                }
                className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
              >
                {services.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            )}
          </div>

          <div className="col-span-1">
            <label className="text-sm font-medium">
              Start (IST)<span className="text-red-500"> *</span>
            </label>
            <input
              type="datetime-local"
              value={form.startISO}
              onChange={(e) =>
                setForm((f) => ({ ...f, startISO: e.target.value }))
              }
              className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <div className="col-span-1">
            <label className="text-sm font-medium">
              End (IST)<span className="text-red-500"> *</span>
            </label>
            <input
              type="datetime-local"
              value={form.endISO}
              onChange={(e) =>
                setForm((f) => ({ ...f, endISO: e.target.value }))
              }
              className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-sm font-medium">
              Notes (visible in email & status page)
            </label>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              rows={3}
              placeholder="e.g., Upgrading models… back by 02:30 IST"
              className="mt-2 w-full border rounded-xl px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className="text-xs text-neutral-500">
            IST only. For emergency work, submit and ping approver.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 rounded-xl bg-black text-white"
              type="button"
            >
              Create Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
